
import React from 'react';
import { NotificationPreferences } from '@/contexts/auth/types';
import { NotificationMethod } from '@/contexts/auth/enums';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Bell, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { requestNotificationPermission } from '@/services/browserNotificationService';

interface NotificationMethodSectionProps {
  preferences: NotificationPreferences;
  onChange: (preferences: NotificationPreferences) => void;
  browserNotificationsSupported: boolean;
  notificationPermission: boolean;
  setNotificationPermission: (value: boolean) => void;
}

const NotificationMethodSection: React.FC<NotificationMethodSectionProps> = ({
  preferences,
  onChange,
  browserNotificationsSupported,
  notificationPermission,
  setNotificationPermission
}) => {
  const handleMethodChange = (notificationMethod: NotificationMethod) => {
    onChange({ ...preferences, notificationMethod });
  };

  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    setNotificationPermission(granted);

    if (granted && (preferences.notificationMethod === NotificationMethod.BROWSER || preferences.notificationMethod === NotificationMethod.BOTH)) {
      const { showBrowserNotification } = await import('@/services/browserNotificationService');
      showBrowserNotification('Medihub értesítések', {
        body: 'Az értesítések sikeresen engedélyezve!',
        icon: '/favicon.ico'
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-8">
        <Bell className="h-4 w-4 text-muted-foreground" />
        <Label className="font-medium">Értesítések módja</Label>
      </div>

      <RadioGroup
        value={preferences.notificationMethod}
        onValueChange={(value) => handleMethodChange(value as NotificationMethod)}
        className="flex flex-col space-y-2"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="email" id="email" />
          <Label htmlFor="email">Email értesítések</Label>
        </div>
        {browserNotificationsSupported && (
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="browser"
              id="browser"
              disabled={!notificationPermission}
            />
            <Label htmlFor="browser" className={!notificationPermission ? "opacity-70" : ""}>
              Böngésző értesítések
            </Label>
            {!notificationPermission && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRequestPermission}
                className="ml-2"
              >
                Engedélyezés
              </Button>
            )}
          </div>
        )}
        {/* <div className="flex items-center space-x-2">
          <RadioGroupItem value="push" id="push" />
          <Label htmlFor="push">Push értesítések (mobilalkalmazás)</Label>
        </div> */}
        {browserNotificationsSupported && (
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="both"
              id="both"
              disabled={!notificationPermission}
            />
            <Label htmlFor="both" className={!notificationPermission ? "opacity-70" : ""}>
              Email és böngésző értesítések
            </Label>
          </div>
        )}
      </RadioGroup>

      {browserNotificationsSupported && !notificationPermission && (
        <div className="flex items-center gap-2 bg-amber-50 text-amber-800 p-3 rounded-md text-sm mt-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <p>Böngésző értesítésekhez engedélyezned kell az értesítéseket.</p>
        </div>
      )}

      {!browserNotificationsSupported && (
        <div className="flex items-center gap-2 bg-amber-50 text-amber-800 p-3 rounded-md text-sm mt-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <p>A böngésződ nem támogatja az értesítéseket.</p>
        </div>
      )}
    </div>
  );
};

export default NotificationMethodSection;
